import { currencySchema, Dtos } from "@inrev/common";
import * as z from "zod";
import { listData, listInput } from "../schemas/fragments";
import { defineTool } from "../tool-definition";

/** A thin code's ratio swings on one claim or recovery, which makes `change` meaningless. */
export const DEFAULT_MIN_EARNED_PREMIUM = 10_000_000;

export const sfaaCodeSortSchema = z.enum(["change", "stressLossRatio", "stressIncurredLoss"]);
export type SfaaCodeSort = z.infer<typeof sfaaCodeSortSchema>;

const rankedCodeSchema = z.strictObject({
	sfaaCode: z.string(),
	line: z.string(),
	subline: z.string(),
	baselineLossRatio: z.number().nullable(),
	stressLossRatio: z.number().nullable(),
	change: z.number().nullable(),
	baselineEarnedPremium: currencySchema,
	stressEarnedPremium: currencySchema,
	stressIncurredLoss: currencySchema,
	peakYear: z.number().int().nullable(),
	peakLossRatio: z.number().nullable(),
});

export const listRankedSfaaCodes = defineTool({
	name: "list_ranked_sfaa_codes",
	description:
		"SFAA class codes ranked by how their loss ratio moved between a baseline window and a stress window, the code-level view below a line or subline. This is not the SFAA code catalog: each row is a comparison computed for the windows you pass, and codes below the premium floor are left out. For example, which contract codes deteriorated most after 2008: baseline 2005-2007, stress 2009-2013. This is industry data, not the record: attribute it to SFAA. Each window's loss ratio is pooled, summed incurred loss over summed earned premium. `change` is the stress ratio minus the baseline ratio, as a fraction (0.25 is 25 points). `sortBy` is `change` (default), `stressLossRatio`, or `stressIncurredLoss` (dollars, which favors large codes). Codes with less than `minEarnedPremium` (whole dollars, default $10M) in either window are left out and counted in `codesBelowPremiumFloor`; lower the floor for a state. Say which windows and floor you used. A negative baseline ratio (recoveries exceeding losses) inflates `change`; say so rather than calling it a spike. Codes come without labels: report them by number and never guess what a code covers. For one code's yearly series, call `get_sfaa_experience` with `sfaaCode`.",
	label: "SFAA codes",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: listInput({
		baselineFromYear: z.number().int(),
		baselineToYear: z.number().int(),
		stressFromYear: z.number().int(),
		stressToYear: z.number().int(),
		line: z.string().min(1).optional(),
		subline: z.string().min(1).optional(),
		location: z.string().min(1).default(Dtos.Admin.SfaaClassExperience.US_TOTAL_LOCATION),
		minEarnedPremium: z.number().nonnegative().default(DEFAULT_MIN_EARNED_PREMIUM),
		sortBy: sfaaCodeSortSchema.default("change"),
	})
		.refine(({ baselineFromYear, baselineToYear }) => baselineFromYear <= baselineToYear, {
			message: "`baselineFromYear` must not be after `baselineToYear`",
			path: ["baselineToYear"],
		})
		.refine(({ stressFromYear, stressToYear }) => stressFromYear <= stressToYear, {
			message: "`stressFromYear` must not be after `stressToYear`",
			path: ["stressToYear"],
		}),
	data: listData(rankedCodeSchema).extend({
		codesBelowPremiumFloor: z.number().int().nonnegative(),
	}),
});
